import { Breakpoints, CTA, styled, TextInput } from '@volkswagen-onehub/components-core';
import { Close, Locate } from '@volkswagen-onehub/icons-core';
import React, { ForwardedRef, RefObject, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useGeolocation } from '../api';
import { getTranslations } from '../translations';

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledInputWrapper = styled.div<{onHomeModal: boolean}>`
  ${
    ({onHomeModal}) => onHomeModal
    ? 'width: 100%;'
    : `
      width: 260px;
      @media (min-width: ${Breakpoints.b560}px) {
        width: 300px;
      }
      @media (min-width: ${Breakpoints.b960}px) {
        width: 320px;
      }
      @media (min-width: ${Breakpoints.b1600}px) {
        width: 400px;
      }
    `
  }
`;

const StyledIconWrapper = styled.div`
  margin-bottom: 8px;
`;

interface Props {
  innerRef?: ForwardedRef<HTMLInputElement>;
  postalCodeValue: string;
  setPostalCodeValue: React.Dispatch<React.SetStateAction<string>>;
  handleEnter: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  hasError: boolean;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
  fetchingGeolocation: boolean;
  setFetchingGeolocation: React.Dispatch<React.SetStateAction<boolean>>;
  onHomeModal?: boolean;
};

const PostalCodeInputInner = ({
  innerRef,
  postalCodeValue,
  setPostalCodeValue,
  hasError,
  setHasError,
  fetchingGeolocation,
  setFetchingGeolocation,
  handleEnter,
  onHomeModal = false
}: Props) => {

  const getLocation = useGeolocation();

  const { ulf_alt, labels } = getTranslations();

  const [errorMessage, setErrorMessage] = useState("");

  const resetErrorState = () => {
    if (hasError) {
      setHasError(false);
      setErrorMessage("");
    }
  };

  const handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    resetErrorState();
    setPostalCodeValue(e.currentTarget.value);
  };

  const handleGeolocate = async (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setFetchingGeolocation(true);

    navigator.geolocation.getCurrentPosition(async (position) => {
      const userPos = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };

      try {
        const response = await getLocation(userPos.lat, userPos.lng);
        if (response) {
          const newPostalCode = (response.parsedBody as google.maps.GeocoderResponse).results[0].address_components.filter(address_component => address_component.types.includes("postal_code"))[0].long_name;
          resetErrorState();
          setPostalCodeValue(newPostalCode);
          setFetchingGeolocation(false);
        }
      } catch(e) {
        // ask for error handling to insert here
        console.log(e);
      }
    }, () => {
      setFetchingGeolocation(false);
      (innerRef as RefObject<HTMLInputElement>)?.current?.focus();
      setErrorMessage(ulf_alt.geolocation_error);
      setHasError(true);
    });
  };

  const handleClear = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setPostalCodeValue("");
    resetErrorState();
    (innerRef as RefObject<HTMLInputElement>)?.current?.focus();
  };

  useEffect(() => {
    if (!fetchingGeolocation) {
      (innerRef as RefObject<HTMLInputElement>)?.current?.focus();
    }
  }, [fetchingGeolocation]);

  useEffect(() => {
    if (hasError && !errorMessage.length) {
      setErrorMessage(ulf_alt.error);
    }
  }, [hasError, errorMessage]);

  return (
    <StyledContainer>
      <StyledInputWrapper onHomeModal={onHomeModal}>
        {
          fetchingGeolocation && !postalCodeValue.length
          ? <Skeleton borderRadius="0" width="100%" height="58px" />
          : <TextInput
            ref={innerRef}
            label={ulf_alt.label}
            placeholder="A1A 1A1"
            value={postalCodeValue}
            onChange={e => handleChange(e)}
            onKeyDown={e => handleEnter(e)}
            appearance={hasError ? "error" : "default"}
            message={errorMessage}
          />
        }
      </StyledInputWrapper>
      <StyledIconWrapper>
        {
          !postalCodeValue.length
          ? <CTA tag="button" emphasis="tertiary" size="small" icon={<Locate ariaLabel={labels.find_location} />} onClick={e => handleGeolocate(e)}/>
          : <CTA tag="button" emphasis="tertiary" size="small" icon={<Close ariaHidden />} onClick={e => handleClear(e)}/>
        }
      </StyledIconWrapper>
    </StyledContainer>
  );
};

export const PostalCodeInput = React.forwardRef<HTMLInputElement, Props>((props, ref) => <PostalCodeInputInner innerRef={ref} {...props} />)
